import { ExperimentOutlined, FilePdfOutlined, LinkOutlined } from '@ant-design/icons';
import { cx } from '@emotion/css';
import { Card, Col, ConfigProvider, Flex, List, Row, Typography } from 'antd';
import DOMPurify from 'dompurify';
import { filesize } from 'filesize';
import type React from 'react';
import { type FragmentType, getFragmentData } from '../../../graphql/generated';
import { useCurrentContextStore } from '../../../hooks/store/useCurrentContextStore';
import { PdfPrintPreview } from '../../../PdfPrintPreview.tsx';
import { htmlContent } from '../../../styles/globalCss.ts';
import { openExternalLink } from '../../../utils/linkOpener.ts';
import { useNewsClassNames } from './News.styles.ts';
import { NewsListFragment } from './NewsList.gql';
import { useAttachmentPreview } from './useAttachmentPreview.ts';

export const NewsList: React.FC<{
  newsFragments: FragmentType<typeof NewsListFragment>[];
  mode: 'dashboard' | 'modal';
}> = ({ newsFragments, mode }) => {
  const newsData = getFragmentData(NewsListFragment, newsFragments);
  const { currentDoctorId } = useCurrentContextStore();
  const classNames = useNewsClassNames();
  const { show: showAttachment, preview } = useAttachmentPreview();

  return (
    <Card
      classNames={{
        body: mode === 'dashboard' ? classNames.newsListDashboard : classNames.newsListModal,
      }}
    >
      {/* the heading of a News entry sits right on its text, without the list title's margin */}
      <ConfigProvider theme={{ components: { List: { titleMarginBottom: 0 } } }}>
        <List
          itemLayout="vertical"
          dataSource={newsData}
          renderItem={item => {
            const showInfoBox =
              item.link1Href || item.link2Href || item.link3Href || item.attachment1Id || item.attachment2Id || item.attachment3Id;

            return (
              <List.Item key={item.id}>
                <List.Item.Meta
                  title={
                    <Flex vertical>
                      <div>
                        <Typography.Text type="secondary">
                          <ExperimentOutlined /> {item.lab.name}
                        </Typography.Text>
                      </div>
                      {item.heading}
                    </Flex>
                  }
                />
                <Row gutter={[20, 20]}>
                  <Col xs={24} lg={showInfoBox ? 12 : 24}>
                    <div
                      className={htmlContent}
                      // biome-ignore lint/security/noDangerouslySetInnerHtml: DOMPurify used
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content) }}
                    />
                  </Col>
                  <Col hidden={!showInfoBox} xs={24} lg={12}>
                    <Card size="small" title="Weiterführende Informationen" className={classNames.sideContent}>
                      {(item.link1Href || item.link2Href || item.link3Href) && (
                        <div className={cx(classNames.mbSm, classNames.mtSm)}>
                          <strong>Links</strong>
                        </div>
                      )}

                      {item.link1Href && (
                        <div>
                          <a
                            onClick={e => {
                              e.preventDefault();
                              openExternalLink(item.link1Href);
                            }}
                          >
                            <LinkOutlined /> {item.link1Text || item.link1Href}
                          </a>
                        </div>
                      )}
                      {item.link2Href && (
                        <div>
                          <a
                            onClick={e => {
                              e.preventDefault();
                              openExternalLink(item.link2Href);
                            }}
                          >
                            <LinkOutlined /> {item.link2Text || item.link2Href}
                          </a>
                        </div>
                      )}
                      {item.link3Href && (
                        <div>
                          <a
                            onClick={e => {
                              e.preventDefault();
                              openExternalLink(item.link3Href);
                            }}
                          >
                            <LinkOutlined /> {item.link3Text || item.link3Href}
                          </a>
                        </div>
                      )}

                      {(item.attachment1Id || item.attachment2Id || item.attachment3Id) && (
                        <div className={cx(classNames.mbSm, classNames.mtSm)}>
                          <strong>Dokumente</strong>
                        </div>
                      )}

                      {item.attachment1Id && (
                        <div>
                          <a
                            onClick={e => {
                              e.preventDefault();
                              showAttachment(
                                item.attachment1Filename,
                                window._env_.API_URL +
                                  `/rest/news-attachment/download/${item.lab.id}/${item.attachment1Id}?doctorId=${currentDoctorId}`
                              );
                            }}
                          >
                            <FilePdfOutlined /> {item.attachment1Filename}{' '}
                          </a>
                          <Typography.Text type="secondary">({filesize(item.attachment1Size, { locale: 'de' })})</Typography.Text>
                        </div>
                      )}
                      {item.attachment2Id && (
                        <div>
                          <a
                            onClick={e => {
                              e.preventDefault();
                              showAttachment(
                                item.attachment2Filename,
                                window._env_.API_URL +
                                  `/rest/news-attachment/download/${item.lab.id}/${item.attachment2Id}?doctorId=${currentDoctorId}`
                              );
                            }}
                          >
                            <FilePdfOutlined /> {item.attachment2Filename}{' '}
                          </a>
                          <Typography.Text type="secondary">({filesize(item.attachment2Size, { locale: 'de' })})</Typography.Text>
                        </div>
                      )}
                      {item.attachment3Id && (
                        <div>
                          <a
                            onClick={e => {
                              e.preventDefault();
                              showAttachment(
                                item.attachment3Filename,
                                window._env_.API_URL +
                                  `/rest/news-attachment/download/${item.lab.id}/${item.attachment3Id}?doctorId=${currentDoctorId}`
                              );
                            }}
                          >
                            <FilePdfOutlined /> {item.attachment3Filename}{' '}
                          </a>
                          <Typography.Text type="secondary">({filesize(item.attachment3Size, { locale: 'de' })})</Typography.Text>
                        </div>
                      )}
                    </Card>
                  </Col>
                </Row>
              </List.Item>
            );
          }}
        />
      </ConfigProvider>

      <PdfPrintPreview {...preview} />
    </Card>
  );
};
